/**
 * File: seo-meta.ts
 *
 * Every page's SEO identity (title, description, canonical URL, social card and
 * structured data) in one pure, dependency-free module. Like articles-meta.ts it
 * is imported in TWO places and therefore must not import anything with a DOM or
 * runtime dependency (no `marked`, no vue, no Vite virtual modules):
 *   - at BUILD time by the `seoAssets` and `prerender` plugins in vite.config.ts,
 *     to generate `sitemap.xml` and the static per-article HTML that link
 *     unfurlers and non-rendering crawlers actually read;
 *   - at RUNTIME by seo.ts, to keep the live document head in step with the
 *     router as the SPA navigates.
 *
 * Both callers must agree, so the values live here rather than in either one.
 *
 * Why the build-time half exists at all: www.bunnyquery.com is served as static
 * files from S3 behind a CloudFront custom error response, so EVERY unknown path
 * returns the same SPA shell. Social crawlers (Slack, LinkedIn, X, Facebook,
 * Discord) do not execute JavaScript, so a head written by seo.ts after mount is
 * invisible to them: without the prerendered copies every shared article link
 * unfurls as the generic home page card.
 */

import type { ArticleMeta } from "./articles-meta.ts";

/** Canonical origin. No trailing slash: `abs()` joins the path. */
export const ORIGIN = "https://www.bunnyquery.com";

export const SITE_NAME = "BunnyQuery";
/**
 * The legal entity behind the site, exactly as the footer and the Terms of
 * Service (public/tspp-bq.html) name it. Used for the Organization legalName
 * and the author meta tag.
 */
export const PUBLISHER = "BROADWAYINC PTE. LTD.";

/**
 * The official profiles the footer links to (src/components/footer.vue), as the
 * Organization's `sameAs`. Keep the two lists in step.
 */
export const SAME_AS = [
    "https://x.com/skapi_api",
    "https://www.linkedin.com/company/skapi-backend-api/",
    "https://www.youtube.com/@skapi_official",
    "https://www.instagram.com/skapi_api",
    "https://www.facebook.com/profile.php?id=61577236221327",
    "https://dev.to/skapi_api",
];

/** Social card. 1200x630, the size Open Graph consumers crop to. */
export const OG_IMAGE = "/ograph.jpg";
export const OG_IMAGE_W = 1200;
export const OG_IMAGE_H = 630;

/** Square mark used as the Organization logo in structured data. */
export const LOGO = "/web-app-manifest-512x512.png";

export const DEFAULT_TITLE = "BunnyQuery | AI Indexed Data Storage";
export const DEFAULT_DESCRIPTION =
    "Upload your documents, spreadsheets, images, notes, and business records. BunnyQuery turns them into a usable database, so your AI can answer questions.";

/** Longest description a crawler will render before truncating it itself. */
const DESCRIPTION_LIMIT = 160;

export interface PageSeo {
    /** Full <title>, already suffixed with the site name where appropriate. */
    title: string;
    description: string;
    /** Absolute canonical URL for this page. */
    canonical: string;
    /** Absolute social card URL. */
    image: string;
    imageAlt: string;
    ogType: "website" | "article";
    /**
     * `robots` content. Undefined on indexable pages so no tag is emitted:
     * the absence of the tag is the "index, follow" default, and an explicit
     * tag would only be one more thing to keep correct.
     */
    robots?: string;
    /** ISO-8601 publication date, articles only. */
    published?: string;
    /** ISO-8601 date of the last content change, articles that declare one. */
    modified?: string;
    /** JSON-LD graph nodes for this page. */
    jsonLd: Record<string, any>[];
}

/** Absolute URL for a site-root-relative path. */
export function abs(path: string): string {
    return path.startsWith("http") ? path : `${ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Clamp a description to what a result snippet will actually show. A snippet
 * that ends on a full sentence reads better than one cut off mid-thought, so
 * the cut prefers the last sentence end that still leaves most of the budget
 * used; failing that it cuts on a word boundary and marks the cut with an
 * ellipsis. Article excerpts arrive pre-truncated at 180 characters (see
 * deriveExcerpt), which is past the limit.
 */
export function clampDescription(text: string, limit = DESCRIPTION_LIMIT): string {
    const flat = text.replace(/\s+/g, " ").trim();
    if (flat.length <= limit) return flat;
    // One character past the limit, so a sentence ending exactly at the limit
    // is still seen as ending (its terminator must be followed by a space).
    const lead = flat.slice(0, limit + 1);
    let sentenceEnd = -1;
    const re = /[.!?](?=\s)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(lead))) sentenceEnd = m.index;
    if (sentenceEnd + 1 >= limit * 0.6) return flat.slice(0, sentenceEnd + 1);
    const cut = flat.slice(0, limit - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

/** `dateSort` (epoch ms, UTC midnight) as a bare ISO date. 0 means undated. */
export function isoDate(dateSort: number): string | undefined {
    return dateSort ? new Date(dateSort).toISOString().slice(0, 10) : undefined;
}

/* ─────────────────────────── structured data ─────────────────────────── */

/** The URL an article declares as its canonical: its own, unless it names another. */
export function articleUrl(article: ArticleMeta): string {
    return article.canonical || `${ORIGIN}/articles/${article.slug}`;
}

/**
 * The publisher node, referenced by @id from every other node so the graph
 * describes one organization rather than repeating it per page.
 */
export function organizationNode(): Record<string, any> {
    return {
        "@type": "Organization",
        "@id": `${ORIGIN}/#organization`,
        name: SITE_NAME,
        legalName: PUBLISHER,
        url: `${ORIGIN}/`,
        logo: {
            "@type": "ImageObject",
            url: abs(LOGO),
            width: 512,
            height: 512,
        },
        sameAs: SAME_AS,
    };
}

export function websiteNode(): Record<string, any> {
    return {
        "@type": "WebSite",
        "@id": `${ORIGIN}/#website`,
        name: SITE_NAME,
        url: `${ORIGIN}/`,
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${ORIGIN}/#organization` },
        inLanguage: "en-US",
    };
}

/**
 * The product itself. `SoftwareApplication` is what makes a SaaS home page
 * eligible for the application rich result; `offers` at price 0 states the free
 * start the home page itself promises ("Free to start. No credit card
 * required."), and nothing it does not.
 */
export function softwareNode(): Record<string, any> {
    return {
        "@type": "SoftwareApplication",
        "@id": `${ORIGIN}/#software`,
        name: SITE_NAME,
        url: `${ORIGIN}/`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${ORIGIN}/#organization` },
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description: "Free to start. No credit card required.",
        },
    };
}

export function breadcrumbNode(trail: { name: string; url: string }[]): Record<string, any> {
    return {
        "@type": "BreadcrumbList",
        itemListElement: trail.map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: t.name,
            item: abs(t.url),
        })),
    };
}

/** The meta description an article gets: its override, else its lead paragraph. */
export function articleDescription(article: ArticleMeta): string {
    return clampDescription(article.seoDescription || article.excerpt || DEFAULT_DESCRIPTION);
}

export function blogPostingNode(article: ArticleMeta): Record<string, any> {
    const published = isoDate(article.dateSort);
    const url = articleUrl(article);
    // The headline stays the visible `# ` heading, which is what Google checks
    // it against; an seo-title only shortens the <title>.
    return {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: article.title,
        description: articleDescription(article),
        url,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        image: abs(OG_IMAGE),
        ...(published
            ? { datePublished: published, dateModified: article.updated || published }
            : {}),
        author: { "@id": `${ORIGIN}/#organization` },
        publisher: { "@id": `${ORIGIN}/#organization` },
        inLanguage: "en-US",
        isAccessibleForFree: true,
    };
}

/** Wrap nodes in a single `@graph` document: one script tag per page. */
export function jsonLdDocument(nodes: Record<string, any>[]): string {
    return JSON.stringify({ "@context": "https://schema.org", "@graph": nodes });
}

/* ────────────────────────────── page SEO ────────────────────────────── */

function base(overrides: Partial<PageSeo> & { canonical: string }): PageSeo {
    return {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        image: abs(OG_IMAGE),
        imageAlt: DEFAULT_TITLE,
        ogType: "website",
        jsonLd: [],
        ...overrides,
        canonical: abs(overrides.canonical),
    };
}

export function homeSeo(): PageSeo {
    return base({
        canonical: "/",
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        jsonLd: [organizationNode(), websiteNode(), softwareNode()],
    });
}

export function articlesListSeo(): PageSeo {
    return base({
        canonical: "/articles",
        title: `Articles and Release Notes | ${SITE_NAME}`,
        description:
            "Product news, release notes and deep dives on how BunnyQuery indexes your files and answers questions from your own data.",
        jsonLd: [
            organizationNode(),
            {
                "@type": "Blog",
                "@id": `${ORIGIN}/articles#blog`,
                name: `${SITE_NAME} Articles`,
                url: `${ORIGIN}/articles`,
                description: "Product news, release notes and technical write-ups from the BunnyQuery team.",
                publisher: { "@id": `${ORIGIN}/#organization` },
                inLanguage: "en-US",
            },
            breadcrumbNode([
                { name: "Home", url: "/" },
                { name: "Articles", url: "/articles" },
            ]),
        ],
    });
}

/**
 * `<title>` for an article: its seo-title override or its heading, with the
 * site-name suffix unless the text already names the site ("What is
 * BunnyQuery? | BunnyQuery" says the name twice and wastes the width).
 */
export function articleTitle(article: ArticleMeta): string {
    const text = article.seoTitle || article.title;
    return text.includes(SITE_NAME) ? text : `${text} | ${SITE_NAME}`;
}

export function articleSeo(article: ArticleMeta): PageSeo {
    return base({
        canonical: articleUrl(article),
        title: articleTitle(article),
        description: articleDescription(article),
        imageAlt: article.title,
        ogType: "article",
        published: isoDate(article.dateSort),
        modified: article.updated,
        jsonLd: [
            organizationNode(),
            blogPostingNode(article),
            breadcrumbNode([
                { name: "Home", url: "/" },
                { name: "Articles", url: "/articles" },
                { name: article.title, url: `/articles/${article.slug}` },
            ]),
        ],
    });
}

/**
 * The account entry pages. They are shared as links (the landing page's "Try
 * BunnyQuery" button, the MCP OAuth hand-off lands on /login), so each keeps a
 * real title and description for the link preview. They are still `noindex`:
 * a login form is not a search result anyone wants, and the page worth ranking
 * for "BunnyQuery" is `/`. The prerender writes static copies of these three,
 * so the noindex is in the bytes S3 serves and not only added by JavaScript.
 */
export const AUTH_ROUTES: Record<string, { path: string; title: string; description: string }> = {
    login: {
        path: "/login",
        title: `Log In | ${SITE_NAME}`,
        description:
            "Log in to BunnyQuery to open your projects, upload files and ask questions about your own data.",
    },
    signup: {
        path: "/signup",
        title: `Sign Up Free | ${SITE_NAME}`,
        description:
            "Create a free BunnyQuery account, upload your documents, spreadsheets and images, and ask AI questions about your own data.",
    },
    forgot: {
        path: "/forgot-password",
        title: `Reset Your Password | ${SITE_NAME}`,
        description: "Reset the password on your BunnyQuery account.",
    },
};

/** Routes that exist but must never reach the index (thin, private or transient). */
const NOINDEX_TITLES: Record<string, string> = {
    "404page": `Page Not Found | ${SITE_NAME}`,
    confirmation: `Confirm Your Email | ${SITE_NAME}`,
    verification: `Email Verification | ${SITE_NAME}`,
    success: `Welcome to ${SITE_NAME}`,
    "change-password": `Change Password | ${SITE_NAME}`,
    "delete-account": `Delete Account | ${SITE_NAME}`,
    "enable-account": `Reactivate Account | ${SITE_NAME}`,
    bye: `Account Closed | ${SITE_NAME}`,
    create: `New Project | ${SITE_NAME}`,
    "delete-service": `Delete Project | ${SITE_NAME}`,
    subscription: `Plan and Billing | ${SITE_NAME}`,
    "account-settings": `Account Settings | ${SITE_NAME}`,
    "my-projects": `My Projects | ${SITE_NAME}`,
    dashboard: `Project Settings | ${SITE_NAME}`,
    users: `Users | ${SITE_NAME}`,
    records: `Records | ${SITE_NAME}`,
    dbfile: `Files | ${SITE_NAME}`,
    agent: `Chat | ${SITE_NAME}`,
};

/**
 * SEO for a resolved route name. `article` is not handled here: its values come
 * from the fetched article, so the detail view calls `articleSeo` itself once the
 * metadata lands.
 *
 * Unknown names fall through to noindex rather than to the home page defaults:
 * a new private route that nobody remembers to list stays out of the index, and
 * the mistake shows up as a missing listing rather than as a duplicate of `/`.
 */
export function seoForRoute(name: string, path: string): PageSeo {
    if (name === "home") return homeSeo();
    if (name === "articles") return articlesListSeo();

    // The article detail view refines this with the real title, excerpt and
    // BlogPosting node once the metadata lands. What matters here is that the
    // canonical is already the article's own URL and that the page is NOT
    // marked noindex while the fetch is in flight.
    if (name === "article") {
        return base({ canonical: path, ogType: "article", jsonLd: [organizationNode()] });
    }

    const auth = AUTH_ROUTES[name];
    if (auth) {
        return base({
            canonical: auth.path,
            title: auth.title,
            description: auth.description,
            robots: "noindex, follow",
            jsonLd: [],
        });
    }

    return base({
        canonical: path,
        title: NOINDEX_TITLES[name] || DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        robots: "noindex, follow",
        jsonLd: [],
    });
}
